import { useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { commercialCyclesApi, GenerateDraftRun } from "@/lib/api";
import { isRolloverScheduleNotFoundError } from "@/lib/cycleError";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { CYCLE_QUERY_KEYS } from "./constants";

/**
 * Lista os agendamentos pendentes (status "Scheduled", de qualquer par de anos) e expõe a
 * ação de cancelar. Ver .claude/docs/frontend_commercial_cycle_rollover_scheduling.md.
 */
export function useRolloverSchedules() {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog: CancelScheduleConfirmDialog } = useConfirmDialog();

  const { data: schedules = [], isLoading } = useQuery<GenerateDraftRun[]>({
    queryKey: CYCLE_QUERY_KEYS.rolloverSchedules,
    queryFn: async () => {
      const response = await commercialCyclesApi.listRolloverSchedules();
      return response.data || [];
    },
    refetchInterval: 15000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: CYCLE_QUERY_KEYS.rolloverSchedules });
    queryClient.invalidateQueries({ queryKey: CYCLE_QUERY_KEYS.generateDraftRuns });
  }, [queryClient]);

  const cancelMutation = useMutation({
    mutationFn: (runId: number) => commercialCyclesApi.cancelRolloverSchedule(runId),
    onSuccess: () => {
      toast.success("Agendamento cancelado com sucesso.");
      invalidate();
    },
    onError: (error: unknown) => {
      if (isRolloverScheduleNotFoundError(error)) {
        toast.error("Agendamento não encontrado — pode já ter sido cancelado ou disparado.");
        invalidate();
        return;
      }
      const response = (error as { response?: { data?: { message?: string } } })?.response;
      toast.error(response?.data?.message || "Erro ao cancelar o agendamento.");
    },
  });

  const handleCancel = useCallback(
    async (schedule: GenerateDraftRun) => {
      const confirmed = await confirm({
        title: `Cancelar agendamento ${schedule.sourceYear} → ${schedule.targetYear}?`,
        description: "O tombamento não será disparado automaticamente na data agendada. Você pode criar um novo agendamento depois, se precisar.",
        confirmLabel: "Cancelar agendamento",
        variant: "destructive",
      });
      if (confirmed) cancelMutation.mutate(schedule.runId);
    },
    [confirm, cancelMutation],
  );

  return {
    schedules,
    isLoading,
    invalidate,
    handleCancel,
    cancelMutation,
    ConfirmDialog: CancelScheduleConfirmDialog,
  };
}
